/** Preserves submission outcomes and hashes so wallet fallback cannot submit the same trade twice. */

import {
  InputEntryFunctionData,
  PendingTransactionResponse,
  SimpleTransaction,
  TransactionResponseType,
} from "@aptos-labs/ts-sdk";
import {
  getTransactionSubmissionFailure,
  submitFeePaidTransaction,
  TransactionSubmissionError,
} from "@decibeltrade/sdk";
import { useMutation } from "@tanstack/react-query";

import { useDecibelWallet } from "@/hooks/use-decibel-wallet";
import {
  isFeePayerNotFoundError,
  isInvalidSignatureError,
  isWalletRejectionError,
} from "@/utils/bridge-errors";
import { getErrorMessage } from "@/utils/error";
import { Logger } from "@/utils/logger";
import { DEFAULT_TXN_EXPIRY_SECONDS, getExpirationTimestamp } from "@/utils/server-time";

import { readDexSdk } from "../dex-sdk";

interface WalletFeePayerArgs {
  data: InputEntryFunctionData;
  expirySeconds?: number;
  onSignSuccess?: () => void;
}

/** Refresh expiry for each prompt while preserving the caller's requested window. */
const selfPaidExpiryOptions = (expirySeconds?: number) => ({
  expireTimestamp: getExpirationTimestamp(expirySeconds ?? DEFAULT_TXN_EXPIRY_SECONDS),
});

/** Wallet adapters may return only a hash; the remaining fields are placeholders until the receipt arrives. */
const pendingResponseFromHash = (hash: string, sender: string): PendingTransactionResponse => ({
  type: TransactionResponseType.Pending,
  hash,
  sender,
  sequence_number: "0",
  max_gas_amount: "0",
  gas_unit_price: "0",
  expiration_timestamp_secs: "0",
  payload: {} as PendingTransactionResponse["payload"],
});

/** Fall back to self-paid signing only when the fee-payer attempt could not have committed. */
export function useWalletFeePayerWithFallback() {
  const { connected, account, signTransaction, signAndSubmitTransaction, isPetraVault } =
    useDecibelWallet();

  return useMutation({
    mutationFn: async (args: WalletFeePayerArgs) => {
      if (!connected || !account) {
        throw new TransactionSubmissionError("Not connected to wallet", {
          outcome: "not-submitted",
        });
      }

      const submitSelfPaid = async (shouldNotifySignSuccess: boolean) => {
        let transactionHash: string | undefined;
        try {
          const pendingTransaction = await signAndSubmitTransaction({
            data: args.data,
            options: selfPaidExpiryOptions(args.expirySeconds),
          });
          transactionHash = pendingTransaction.hash;
          if (shouldNotifySignSuccess) args.onSignSuccess?.();
          return pendingResponseFromHash(pendingTransaction.hash, account.address.toString());
        } catch (error) {
          // A combined wallet call may submit before throwing; retain any known outcome and hash.
          const submissionFailure = getTransactionSubmissionFailure(error);
          throw new TransactionSubmissionError(
            getErrorMessage(error) ?? "Unknown error",
            {
              outcome: submissionFailure?.outcome ?? "unknown",
              transactionHash: submissionFailure?.transactionHash ?? transactionHash,
            },
            error,
          );
        }
      };

      if (isPetraVault) return submitSelfPaid(true);

      const aptos = readDexSdk.deps.aptos;

      // Use server time because the device clock may be wrong.
      const expirationTimestamp = getExpirationTimestamp(
        args.expirySeconds ?? DEFAULT_TXN_EXPIRY_SECONDS,
      );

      let feePayerTransaction: SimpleTransaction;
      try {
        feePayerTransaction = await aptos.transaction.build.simple({
          sender: account.address,
          data: args.data,
          withFeePayer: true,
          options: {
            expireTimestamp: expirationTimestamp,
          },
        });
      } catch (error) {
        throw new TransactionSubmissionError(
          getErrorMessage(error) ?? "Unknown error",
          { outcome: "not-submitted" },
          error,
        );
      }

      let signedFeePayerEnvelope = false;
      let feePayerSubmissionStarted = false;
      try {
        const signedTransaction = await signTransaction({
          transactionOrPayload: feePayerTransaction,
        });
        signedFeePayerEnvelope = true;
        args.onSignSuccess?.();
        feePayerSubmissionStarted = true;
        return await submitFeePaidTransaction(
          readDexSdk.config,
          feePayerTransaction,
          signedTransaction.authenticator,
        );
      } catch (error) {
        // Unsupported fee-payer signing, INVALID_SIGNATURE, and a missing gas-station fee payer
        // cannot commit; other submit errors may.
        const feePayerSubmissionRuledOut =
          !signedFeePayerEnvelope ||
          isInvalidSignatureError(error) ||
          isFeePayerNotFoundError(error);
        if (isWalletRejectionError(error) || !feePayerSubmissionRuledOut) {
          throw new TransactionSubmissionError(
            getErrorMessage(error) ?? "Unknown error",
            getTransactionSubmissionFailure(error) ?? {
              outcome: feePayerSubmissionStarted ? "unknown" : "not-submitted",
            },
            error,
          );
        }

        Logger.error(error, {
          message: "Fee-payer transaction failed; falling back to self-paid transaction",
          signedFeePayerEnvelope,
          function: args.data.function,
        });

        // Notify once even when a signed fee-payer attempt needs a second wallet prompt.
        return submitSelfPaid(!signedFeePayerEnvelope);
      }
    },
  });
}
